// ==================== PENDING APPROVAL PAGE ====================

import { motion } from 'framer-motion';
import { Clock, Mail, Phone, RefreshCw, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Logo } from '../../components/ui/Logo';
import { useAuth } from '../../contexts';
import { APP_CONSTANTS } from '../../constants';
import { fadeInUp, staggerContainer, pulseTransition } from '../../utils/animations';

// ==================== MAIN COMPONENT ====================

const PendingApprovalPage = () => {
  const { user, logout, refreshUser, isLoading } = useAuth();

  const handleRefresh = async () => {
    await refreshUser();
    // If user becomes active after refresh, AuthContext will redirect
    if (user?.isActive) {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-(--color-dark-navy) flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-(--color-signal-orange)/5 rounded-full blur-3xl"
          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-(--color-warm-amber)/5 rounded-full blur-3xl"
          animate={{ scale: [1.1, 1, 1.1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
      </div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="relative z-10 w-full max-w-lg"
      >
        {/* Card */}
        <div className="bg-(--color-charcoal-black)/80 backdrop-blur-md rounded-2xl border border-(--color-border-gray) p-8 lg:p-10">
          {/* Logo */}
          <motion.div variants={fadeInUp} className="flex justify-center mb-6">
            <Logo size="xl" />
          </motion.div>

          {/* Animated Clock Icon */}
          <motion.div
            variants={fadeInUp}
            className="flex justify-center mb-6"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={pulseTransition}
              className="w-24 h-24 rounded-full bg-(--color-warning)/20 flex items-center justify-center"
            >
              <Clock size={48} className="text-(--color-warning)" />
            </motion.div>
          </motion.div>

          {/* Title */}
          <motion.div variants={fadeInUp} className="text-center mb-6">
            <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white mb-3">
              Account Pending Approval
            </h1>
            <p className="text-(--color-soft-gray)">
              Thank you for registering, <span className="text-white font-medium">{user?.firstName}</span>! 
              Your account is currently under review.
            </p>
          </motion.div>

          {/* Status Info */}
          <motion.div
            variants={fadeInUp}
            className="bg-(--color-navy-light) rounded-xl p-5 mb-6 border border-(--color-border-gray)"
          >
            <h3 className="text-sm font-medium text-white mb-3">What happens next?</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm">
                <span className="w-6 h-6 rounded-full bg-(--color-signal-orange)/20 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-(--color-signal-orange) text-xs font-bold">1</span>
                </span>
                <span className="text-(--color-soft-gray)">
                  Our team will verify your organization details
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm">
                <span className="w-6 h-6 rounded-full bg-(--color-signal-orange)/20 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-(--color-signal-orange) text-xs font-bold">2</span>
                </span>
                <span className="text-(--color-soft-gray)">
                  You'll receive an email once your account is approved
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm">
                <span className="w-6 h-6 rounded-full bg-(--color-signal-orange)/20 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-(--color-signal-orange) text-xs font-bold">3</span>
                </span>
                <span className="text-(--color-soft-gray)">
                  Choose a subscription plan and start growing your business
                </span>
              </li>
            </ul>
          </motion.div>

          {/* Account Details */}
          {user && (
            <motion.div
              variants={fadeInUp}
              className="bg-(--color-midnight-navy) rounded-xl p-4 mb-6 border border-(--color-border-gray)"
            >
              <p className="text-xs text-(--color-muted-gray) mb-2">Registered Account</p>
              <p className="text-white font-medium">{user.firstName} {user.lastName}</p>
              <p className="text-sm text-(--color-soft-gray)">{user.email}</p>
              {user.organization && (
                <p className="text-sm text-(--color-muted-gray) mt-1">{user.organization.name}</p>
              )}
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div variants={fadeInUp} className="space-y-3">
            <Button
              variant="primary"
              fullWidth
              onClick={handleRefresh}
              isLoading={isLoading}
              leftIcon={<RefreshCw size={18} />}
            >
              Check Approval Status
            </Button>
            
            <Button
              variant="ghost"
              fullWidth
              onClick={logout}
              leftIcon={<LogOut size={18} />}
            >
              Sign Out
            </Button>
          </motion.div>

          {/* Contact Info */}
          <motion.div variants={fadeInUp} className="mt-6 pt-6 border-t border-(--color-border-gray)">
            <p className="text-xs text-(--color-muted-gray) text-center mb-3">
              Need help or have questions?
            </p>
            <div className="flex items-center justify-center gap-4">
              <a
                href={`mailto:${APP_CONSTANTS.CONTACT_EMAIL}`}
                className="flex items-center gap-2 text-sm text-(--color-soft-gray) hover:text-(--color-signal-orange) transition-colors"
              >
                <Mail size={14} />
                {APP_CONSTANTS.CONTACT_EMAIL}
              </a>
              <span className="text-(--color-border-gray)">|</span>
              <a
                href={`tel:${APP_CONSTANTS.CONTACT_PHONE}`}
                className="flex items-center gap-2 text-sm text-(--color-soft-gray) hover:text-(--color-signal-orange) transition-colors"
              >
                <Phone size={14} />
                {APP_CONSTANTS.CONTACT_PHONE}
              </a>
            </div>
          </motion.div>
        </div>

        {/* Footer Note */}
        <motion.p
          variants={fadeInUp}
          className="text-center text-xs text-(--color-muted-gray) mt-6"
        >
          Approval typically takes 24-48 hours during business days.
        </motion.p>
      </motion.div>
    </div>
  );
};

export { PendingApprovalPage };

